import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import { theme, media } from 'styles';
import { Container, SeoTags, RelatedCaseStudies } from 'components/shared';
import { Header, Testimonial } from './components';

const { sp, colors } = theme;

const Wrapper = styled.div`
  background-color: ${colors.white};
  margin-bottom: 100px;

  ${media.laptop`
    margin-bottom: 160px;
  `};
`;

const FeatureImages = styled.div`
  padding: 0 0 40px;

  ${media.tablet`
    padding: 0 0 60px;
  `};
`;

const FeatureImagesWrapper = styled.div`
  display: flex;
`;

const FeatureImage = styled.div`
  position: relative;
  align-self: flex-start;

  ${media.tablet`
  `};

  &:nth-child(1) {
    margin-top: 15%;
  }

  &:nth-child(2) {
  }

  &:nth-child(3) {
    margin-top: 30%;
    left: -12%;
  }

  img {
    display: block;
    width: 136%;
    max-width: 136%;
    border: 5px solid ${colors.white};

    ${media.tablet`
      border: 10px solid ${colors.white};
    `};
  }
`;

const Content = styled.div`
  font-size: 20px;
  font-weight: 400;
  margin: 0 auto;

  h2 {
    margin-bottom: ${sp.m};
    font-size: 30px;
    line-height: 36px;

    ${media.tablet`
      font-size: 36px;
      line-height: 43px;
    `};
  }

  p,
  ul,
  ol {
    counter-reset: item;
    line-height: 1.4;
    font-weight: 400;
    margin-bottom: ${sp.xl};

    ${media.tablet`
      margin-bottom: ${sp.xxl};
    `};
  }

  ul li {
    position: relative;
    list-style: none;
    padding-left: 40px;
    margin-bottom: ${sp.s};

    &::before {
      position: absolute;
      top: 15px;
      left: 0;
      width: 20px;
      height: 2px;
      background-color: ${colors.blue};
      content: '';
    }
  }

  ol li {
    position: relative;
    counter-increment: item;
    list-style: none;
    padding-left: 40px;
    margin-bottom: ${sp.s};

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      content: counters(item, '') '.';
      color: ${colors.blue};
      width: 20px;
    }
  }

  a {
    transition: all 0.3s;
    text-decoration: none;
    color: ${colors.blue};
  }

  a:hover {
    color: ${colors.blue};
  }

  blockquote {
    margin: ${sp.max} 0 100px;
    border-left: ${sp.xxs} solid ${colors.blue};

    p {
      margin: 0;
      padding-left: 80px;
      width: 100%;
      padding-right: 100px;
      font-size: 24px;
      line-height: 36px;
      font-style: italic;
      font-weight: 500;
      color: ${colors.grey};
    }
  }

  .embed-container {
    margin-bottom: ${sp.xxl};
  }

  pre {
    margin: 0 0 ${sp.xl};
    font-size: 44px;
    line-height: 44px;
    font-weight: 700;
    color: ${colors.royal};
    white-space: normal;
    text-align: center;

    ${media.tablet`
      margin: 0 0 ${sp.xl};
      font-size: 64px;
      line-height: 64px;
    `};
  }

  code {
    font-family: 'sofia-pro', 'Helvetica Neue', Helvetica, sans-serif;
  }

  img {
    display: block;
    max-width: 100%;
    margin: 0 auto ${sp.xxl};
  }
`;

const WorkMeta = styled.div`
  background-color: ${colors.lightBlue};
  margin-bottom: ${sp.xxl};
  padding: 100px 0 60px;

  ${media.tablet`
    padding: 100px 0;
  `};

  h4 {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: ${sp.s};

    ${media.tablet`
      font-size: 24px;
      line-height: 32px;
      margin-bottom: ${sp.s};
    `};

    span {
      color: ${colors.grey};
    }
  }
`;

const RelatedWorkWrapper = styled.div`
  background-color: ${colors.lightBlue};
  padding-top: 100px;
`;

const RelatedWork = styled.div`
  h2 {
    font-size: 48px;
    line-height: 58px;
    font-weight: 500;
    color: ${colors.navy};
    text-align: center;
  }

  h3 {
    margin-bottom: ${sp.xxl};
    font-size: 24px;
    line-height: 32px;
    font-weight: 500;
    color: ${colors.navy};
    text-align: center;
  }
`;

const TestimonialWrapper = styled.div``;

function CaseStudyPage({ data }) {
  const {
    heading,
    slug,
    excerpt,
    content,
    contentAfter,
    featureImages,
    headerBackgroundImage,
    industry,
    services,
    testimonial,
    relatedCaseStudies,
    seoTitle,
    seoKeywords,
    seoDescription,
  } = data;
  return (
    <Wrapper>
      <SeoTags
        title={seoTitle}
        keywords={seoKeywords}
        description={seoDescription && seoDescription.seoDescription}
      />

      <Header
        heading={heading}
        excerpt={excerpt.excerpt}
        slug={slug}
        image={headerBackgroundImage}
      />

      <WorkMeta>
        <Container narrow>
          <h4>
            <span>Client:</span> {heading}
          </h4>
          {industry && (
            <h4>
              <span>Industry:</span> {industry}
            </h4>
          )}
          {services && (
            <h4>
              <span>Services:</span> {services.services}
            </h4>
          )}
        </Container>
      </WorkMeta>

      <Content>
        <Container narrow>
          <ReactMarkdown source={content.content} escapeHtml={false} />
        </Container>
      </Content>

      <FeatureImages>
        <Container>
          <FeatureImagesWrapper>
            {featureImages &&
              featureImages.map(featureImage => (
                <FeatureImage key={featureImage.id}>
                  <img src={featureImage.fullSize.src} alt="" />
                </FeatureImage>
              ))}
          </FeatureImagesWrapper>
        </Container>
      </FeatureImages>

      {contentAfter && (
        <Content>
          <Container narrow>
            <ReactMarkdown
              source={contentAfter.contentAfter}
              escapeHtml={false}
            />
          </Container>
        </Content>
      )}

      <TestimonialWrapper>
        <Container>
          {testimonial && <Testimonial testimonial={testimonial} />}
        </Container>
      </TestimonialWrapper>

      <RelatedWorkWrapper>
        <Container>
          {relatedCaseStudies && (
            <RelatedWork>
              <h2>See how we’ve done it for others.</h2>
              <h3>Sell more by effectively marketing your business.</h3>
              <RelatedCaseStudies relatedCaseStudies={relatedCaseStudies} />
            </RelatedWork>
          )}
        </Container>
      </RelatedWorkWrapper>
    </Wrapper>
  );
}

CaseStudyPage.propTypes = {
  data: PropTypes.shape({}).isRequired,
};

export default CaseStudyPage;



// WEBPACK FOOTER //
// ./src/components/CaseStudyPage/CaseStudyPage.jsx